import { Button, IconButton } from '../../components/Button';
import { useSound } from '../../hooks/useSound';
import type { BoardProps } from '../../engine/types';
import { cx } from '../../utils/classnames';
import { forgeRank, isPangram, type ForgeMove, type ForgeState } from './logic';

export function ForgeBoard({ state, status, dispatch, feedback }: BoardProps<ForgeState, ForgeMove>) {
  const playing = status === 'playing';
  const sound = useSound();
  const rank = forgeRank(state);
  const errorNonce = feedback?.tone === 'error' ? feedback.nonce : 0;

  const tapLetter = (ch: string) => {
    sound.play('select');
    dispatch({ type: 'add', ch });
  };

  return (
    <div className="cv-board">
      <div className="cv-forge">
        <div
          key={`entry-${errorNonce}`}
          className={cx('cv-forge__entry', errorNonce && 'cv-anim-shake')}
          aria-live="polite"
          aria-label={state.current ? `Current word ${state.current}` : 'No letters yet'}
        >
          {state.current.length === 0 ? (
            <span className="cv-faint">type or tap letters</span>
          ) : (
            state.current.split('').map((ch, i) => (
              <em key={i} data-center={ch === state.center ? 'true' : undefined}>
                {ch.toUpperCase()}
              </em>
            ))
          )}
          {playing && <span className="caret" aria-hidden="true" />}
        </div>

        <div className="cv-forge__ring" role="group" aria-label="Letter set">
          {state.letters.slice(1).map((ch) => (
            <button
              key={ch}
              type="button"
              className="cv-forge__cell"
              onClick={() => tapLetter(ch)}
              disabled={!playing}
              aria-label={`Letter ${ch}`}
            >
              {ch.toUpperCase()}
            </button>
          ))}
          <button
            type="button"
            className="cv-forge__center"
            onClick={() => tapLetter(state.center)}
            disabled={!playing}
            aria-label={`Center letter ${state.center}, required`}
          >
            {state.center.toUpperCase()}
          </button>
        </div>

        <div className="cv-hintbar">
          <IconButton
            icon="back"
            label="Delete"
            onClick={() => {
              sound.play('delete');
              dispatch({ type: 'remove' });
            }}
            disabled={!playing || state.current.length === 0}
          />
          <Button
            variant="primary"
            onClick={() => {
              sound.play('submit');
              dispatch({ type: 'submit' });
            }}
            disabled={!playing}
          >
            Enter
          </Button>
          <IconButton
            icon="shuffle"
            label="Shuffle letters"
            onClick={() => {
              sound.play('select');
              dispatch({ type: 'shuffle' });
            }}
            disabled={!playing}
          />
        </div>

        <div className="cv-forge__rank">
          <div className="cv-row-between" style={{ marginBottom: 'var(--space-2xs)' }}>
            <span className="cv-eyebrow">{rank.label}</span>
            <span className="cv-mono cv-faint">
              {state.found.length}/{state.target} to pass
            </span>
          </div>
          <div
            style={{
              height: 8,
              borderRadius: 'var(--radius-pill)',
              background: 'var(--paper-sunken)',
              overflow: 'hidden',
            }}
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(rank.fraction * 100)}
          >
            <div
              style={{
                height: '100%',
                width: `${Math.max(3, rank.fraction * 100)}%`,
                background: 'var(--vine)',
                borderRadius: 'var(--radius-pill)',
                transition: 'width var(--dur-slow) var(--ease-out)',
              }}
            />
          </div>
        </div>

        <div className="cv-forge__found" role="list" aria-label="Found words">
          {state.found.map((w) => (
            <span
              key={w}
              role="listitem"
              className="cv-forge__word"
              data-pangram={isPangram(w, state.letters) ? 'true' : undefined}
            >
              {w}
            </span>
          ))}
        </div>

        {playing && state.found.length > 0 && (
          <Button
            variant={state.found.length >= state.target ? 'primary' : 'ghost'}
            iconLeft={state.found.length >= state.target ? 'check' : undefined}
            onClick={() => dispatch({ type: 'finish' })}
          >
            {state.found.length >= state.target ? 'Finish — you passed!' : 'Finish round'}
          </Button>
        )}
      </div>

      <div className="cv-board__status" data-tone={feedback?.tone === 'error' ? 'error' : undefined} aria-hidden="true">
        {feedback?.message ?? ''}
      </div>
      <p className="visually-hidden" aria-live="polite">
        {feedback?.message}
      </p>
    </div>
  );
}
